import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, TableProperties } from "lucide-react";
import type { PolicyLoanLedgerRow, StandardLoanAmortizationRow, PolicyLoanPayment } from "@/lib/calculationEngine";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";
import { ScheduleExportPanel } from "@/components/ScheduleExportPanel";

interface ScheduleTabsProps {
  policyLedger: PolicyLoanLedgerRow[];
  amortization: StandardLoanAmortizationRow[];
  plannedPayments: PolicyLoanPayment[];
  mode: 'advisor' | 'client';
  jurisdiction: Jurisdiction;
  policyNumber?: string;
  loanTitle?: string;
}

function fmtRate(n: number): string {
  return (n * 100).toFixed(2) + '%';
}

export function ScheduleTabs({ policyLedger, amortization, plannedPayments, mode, jurisdiction, policyNumber, loanTitle }: ScheduleTabsProps) {
  const { fmt } = createFormatter(jurisdiction);
  const [isOpen, setIsOpen] = useState(false);
  const policyTaxInterestByYear = policyLedger.reduce<Record<string, number>>((acc, row) => {
    const year = row.date.slice(0, 4);
    acc[year] = (acc[year] ?? 0) + row.intervalInterestAccrued;
    return acc;
  }, {});

  const standardTaxInterestByYear = amortization.reduce<Record<string, number>>((acc, row) => {
    const year = row.paymentDate.slice(0, 4);
    acc[year] = (acc[year] ?? 0) + row.interestPortion;
    return acc;
  }, {});

  const lastStandardRowIndexByYear = amortization.reduce<Record<string, number>>((acc, row, index) => {
    acc[row.paymentDate.slice(0, 4)] = index;
    return acc;
  }, {});

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer select-none hover:bg-muted/30 transition-colors pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <TableProperties className="h-4 w-4" />
              {isOpen ? "Loan Schedules" : "View Loan Schedules"}
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
        <CardContent>
        <ScheduleExportPanel
          data={{ policyLedger, amortization, plannedPayments }}
          meta={{ policyNumber, loanTitle, jurisdiction }}
          mode={mode}
        />
        <Tabs defaultValue={mode === 'advisor' ? 'policy' : 'commitment'}>
          <TabsList className="mb-4 flex-wrap">
            {mode === 'advisor' && <TabsTrigger value="policy">Policy Loan Ledger</TabsTrigger>}
            <TabsTrigger value="standard">Intended Amortization</TabsTrigger>
            <TabsTrigger value="commitment">Commitment Schedule</TabsTrigger>
          </TabsList>

          {mode === 'advisor' && (
            <TabsContent value="policy">
              <div className="max-h-96 overflow-auto -mx-2 sm:mx-0">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Days</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Rate</TableHead>
                      <TableHead className="text-right">Interest</TableHead>
                      <TableHead className="text-right hidden md:table-cell">Dec 31 Interest (Tax Year)</TableHead>
                      <TableHead className="text-right">Payment</TableHead>
                      <TableHead className="text-right hidden md:table-cell">Capitalized</TableHead>
                      <TableHead className="text-right hidden lg:table-cell">Principal</TableHead>
                      <TableHead className="text-right hidden lg:table-cell">Accrued Int.</TableHead>
                      <TableHead className="text-right">Total Owing</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policyLedger.map((row, i) => {
                      const year = row.date.slice(0, 4);
                      const isYearEndSummary = row.eventType === 'year_end_summary';
                      const isCapitalization = row.eventType === 'capitalization';
                      const displayAccrued = Math.abs(row.endingAccruedInterest) < 0.01 ? 0 : row.endingAccruedInterest;
                      const displayTotal = Math.abs(row.totalIndebtednessAfterEvent) < 0.01 ? 0 : row.totalIndebtednessAfterEvent;

                      return (
                        <TableRow
                          key={i}
                          className={
                            row.lapseRiskWarning
                              ? 'bg-destructive/5'
                              : isCapitalization
                              ? 'bg-amber-50 dark:bg-amber-950/30 border-l-4 border-l-amber-500'
                              : isYearEndSummary
                              ? 'bg-accent/5'
                              : ''
                          }
                        >
                          <TableCell className="tabular-nums text-xs">{row.date}</TableCell>
                          <TableCell className={`text-xs ${isCapitalization ? 'font-semibold text-amber-700 dark:text-amber-400' : ''}`}>
                            {isYearEndSummary
                              ? 'Dec 31 summary'
                              : isCapitalization
                              ? 'Policy Anniversary Date Capitalization'
                              : row.eventType.replace(/_/g, ' ')}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-xs hidden sm:table-cell">{row.daysInInterval}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs hidden sm:table-cell">{fmtRate(row.annualRateUsed)}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs">{fmt(row.intervalInterestAccrued)}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs font-medium hidden md:table-cell">
                            {isYearEndSummary ? fmt(policyTaxInterestByYear[year] ?? 0) : '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-xs">{row.paymentAmount > 0 ? fmt(row.paymentAmount) : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs hidden md:table-cell">{row.capitalizedInterest > 0 ? fmt(row.capitalizedInterest) : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs hidden lg:table-cell">{fmt(row.endingPrincipal)}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs hidden lg:table-cell">{fmt(displayAccrued)}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs font-medium">{fmt(displayTotal)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          )}

          <TabsContent value="standard">
            <div className="max-h-96 overflow-auto -mx-2 sm:mx-0">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Payment</TableHead>
                    <TableHead className="text-right">Interest</TableHead>
                    <TableHead className="text-right hidden sm:table-cell">Principal</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right hidden md:table-cell">Dec 31 Interest (Tax Year)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {amortization.map((r, i) => {
                    const year = r.paymentDate.slice(0, 4);
                    const isYearEndRow = lastStandardRowIndexByYear[year] === i;

                    return (
                      <TableRow key={r.paymentNumber} className={isYearEndRow ? 'bg-accent/5' : ''}>
                        <TableCell className="tabular-nums">{r.paymentNumber}</TableCell>
                        <TableCell className="tabular-nums">{r.paymentDate}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(r.paymentAmount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(r.interestPortion)}</TableCell>
                        <TableCell className="text-right tabular-nums hidden sm:table-cell">{fmt(r.principalPortion)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(r.remainingBalance)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium hidden md:table-cell">
                          {isYearEndRow ? fmt(standardTaxInterestByYear[year] ?? 0) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="commitment">
            <div className="max-h-96 overflow-auto -mx-2 sm:mx-0">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Payment Amount</TableHead>
                    <TableHead className="text-center">☐</TableHead>
                    <TableHead className="hidden sm:table-cell">Date Paid</TableHead>
                    <TableHead className="hidden sm:table-cell">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plannedPayments.map((p, i) => (
                    <TableRow key={p.id}>
                      <TableCell className="tabular-nums">{i + 1}</TableCell>
                      <TableCell className="tabular-nums">{p.date}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(p.amount)}</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="hidden sm:table-cell"></TableCell>
                      <TableCell className="hidden sm:table-cell"></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      </CollapsibleContent>
    </Card>
    </Collapsible>
  );
}
